import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { aboutStats, faqs } from "@/mocks/comics";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = formData.get("website_alt") as string;
    if (honeypot && honeypot.trim() !== "") {
      setFormStatus("success");
      form.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setFormStatus("loading");
    setFormError("");

    try {
      const urlParams = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== "website_alt") {
          urlParams.append(key, value as string);
        }
      });

      const response = await fetch("https://readdy.ai/api/form/d9ote4iqs38sj6jc7fdg", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: urlParams.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (
          typeof serverMsg === "string" &&
          (serverMsg.includes("spam") || serverMsg.includes("form data is spam"))
        ) {
          setFormStatus("success");
          form.reset();
          setTimeout(() => setFormStatus("idle"), 3000);
        } else {
          setFormError(typeof serverMsg === "string" ? serverMsg : "提交失败，请稍后再试");
          setFormStatus("error");
        }
      }
    } catch {
      setFormError("网络错误，请检查连接后重试");
      setFormStatus("error");
    }
  };

  return (
    <main>
      {/* Organization Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "073漫画网",
            url: "https://073manga.com",
            description:
              "专注韩国漫画的免费在线阅读平台，提供无删减下拉式阅读体验。5000+部韩漫资源，每日更新，完全免费。",
            foundingDate: "2022",
            contactPoint: {
              "@type": "ContactPoint",
              email: "contact@073manga.com",
              contactType: "customer service",
              availableLanguage: "Chinese",
            },
          }),
        }}
      />

      {/* BreadcrumbList Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "首页",
                item: "https://073manga.com",
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "关于我们",
                item: "https://073manga.com/about",
              },
            ],
          }),
        }}
      />

      <Navbar />

      {/* Page Header */}
      <section className="pt-24 pb-8 bg-background-100">
        <div className="w-full max-w-[1024px] mx-auto px-4 md:px-6">
          <nav className="flex items-center gap-2 text-xs text-foreground-400 mb-4" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-600">关于我们</span>
          </nav>

          <h1 className="text-3xl md:text-4xl font-bold text-foreground-950 font-heading">
            关于073漫画网
          </h1>
          <p className="text-sm text-foreground-500 mt-3 max-w-2xl">
            专注韩国漫画的免费在线阅读平台，致力于为韩漫爱好者提供最优质的阅读体验
          </p>
        </div>
      </section>

      {/* Brand Introduction */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1024px] mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 items-center">
            <div className="flex-1 min-w-0">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading mb-4">
                品牌介绍
              </h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                073漫画网成立于2022年，是国内领先的<strong>韩国漫画在线免费阅读平台</strong>。我们深知韩漫爱好者对优质内容的渴望，因此从创立之初就确立了三大核心理念：<strong>完全免费、无删减内容、下拉式阅读体验</strong>。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                我们的团队由一群热爱漫画的年轻人组成，每天都在不断扩充漫画资源库，现已收录<strong>超过5000部韩国漫画</strong>，涵盖恋爱、热血、奇幻、剧情、搞笑、悬疑、BL、GL等十多个分类频道。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                在更新速度上，073漫画网始终力求与韩国保持同步。热门连载作品基本<strong>每周更新一话</strong>，让读者无需漫长等待。我们采用下拉式阅读技术，完美适配手机和电脑，让您随时随地畅享韩漫。
              </p>
            </div>
            <div className="flex-shrink-0 w-full lg:w-[380px]">
              <div className="aspect-[4/3] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20cozy%20modern%20reading%20space%20with%20a%20tablet%20displaying%20colorful%20webtoon%20art%2C%20surrounded%20by%20books%20and%20a%20warm%20lamp%2C%20soft%20natural%20light%20from%20window%2C%20comfortable%20armchair%2C%20warm%20amber%20and%20cream%20tones%2C%20inviting%20atmosphere%2C%20lifestyle%20photography%20style&width=800&height=600&seq=about-brand-img&orientation=landscape"
                  alt="073漫画网品牌形象"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full max-w-[1024px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              平台数据
            </h2>
            <p className="text-sm text-foreground-500 mt-2">用数字说话，见证073漫画网的成长</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {aboutStats.map((stat) => (
              <div
                key={stat.label}
                className="p-6 rounded-2xl bg-background-50 border border-background-200/60 text-center"
              >
                <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-600 mb-3">
                  <i className={`${stat.icon} text-xl`}></i>
                </span>
                <div className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
                  {stat.value}
                </div>
                <div className="text-xs text-foreground-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[1024px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 mt-2">来自真实读者的评价</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {[
              {
                name: "小樱",
                text: "用了073漫画网之后就没换过别的平台了！更新速度真的快，而且完全免费，下拉式阅读在手机上超舒服。",
                rating: 5,
              },
              {
                name: "漫画迷阿杰",
                text: "韩漫资源超齐全！之前在其他平台找不到的无删减版这里都有，而且画质很好，强烈推荐！",
                rating: 5,
              },
              {
                name: "深夜读书人",
                text: "没有任何广告弹窗干扰，阅读体验一流。分类也很清晰，找漫画特别方便。已经推荐给身边所有喜欢韩漫的朋友了。",
                rating: 5,
              },
            ].map((review, i) => (
              <div
                key={i}
                className="p-6 rounded-2xl bg-background-100 border border-background-200/60"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: review.rating }, (_, j) => (
                    <span key={j} className="w-4 h-4 flex items-center justify-center text-accent-500">
                      <i className="ri-star-fill text-sm"></i>
                    </span>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  「{review.text}」
                </p>
                <p className="text-xs font-medium text-foreground-400">—— {review.name}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="w-full max-w-[640px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 mt-2">
              有任何问题或建议？欢迎随时与我们联系
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            data-readdy-form
            className="flex flex-col gap-4"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-name" className="text-xs font-medium text-foreground-600">
                  姓名
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的姓名"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="contact-email" className="text-xs font-medium text-foreground-600">
                  邮箱
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                  placeholder="请输入您的邮箱"
                />
              </div>
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-subject" className="text-xs font-medium text-foreground-600">
                主题
              </label>
              <input
                id="contact-subject"
                name="subject"
                type="text"
                required
                className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors"
                placeholder="请输入咨询主题"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="contact-message" className="text-xs font-medium text-foreground-600">
                留言内容
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                className="px-4 py-2.5 bg-background-50 border border-background-200 rounded-xl text-sm text-foreground-800 placeholder-foreground-400 outline-none focus:border-primary-300 transition-colors resize-none"
                placeholder="请输入您的留言内容（500字以内）"
              ></textarea>
              <div className="text-xs text-foreground-400 text-right">最多500个字符</div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              className="form-website-alt"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            <button
              type="submit"
              disabled={formStatus === "loading"}
              className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
            >
              {formStatus === "loading" ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  提交中...
                </>
              ) : (
                "发送留言"
              )}
            </button>

            {formStatus === "success" && (
              <div className="text-sm text-emerald-600 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-center">
                <i className="ri-checkbox-circle-line mr-1"></i>
                留言已成功发送！我们会尽快回复您。
              </div>
            )}
            {formStatus === "error" && (
              <div className="text-sm text-foreground-600 bg-background-50 border border-background-200 rounded-xl px-4 py-3 text-center">
                {formError}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
          <div className="text-center mb-10">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-950 font-heading">
              常见问题
            </h2>
          </div>
          <div className="flex flex-col gap-3">
            {faqs.slice(0, 4).map((faq, index) => (
              <div
                key={index}
                className="rounded-xl bg-background-100 border border-background-200/60 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left"
                >
                  <span className="text-sm font-medium text-foreground-800">{faq.question}</span>
                  <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i
                      className={`text-foreground-400 transition-transform duration-300 ${
                        openFaq === index ? "ri-subtract-line" : "ri-add-line"
                      }`}
                    ></i>
                  </span>
                </button>
                {openFaq === index && (
                  <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
}